import api from './api'

/**
 * Project Mindmap Service
 * API calls for project mindmap feature
 */

// Helper to check if user is authenticated
const isAuthenticated = () => {
  return !!localStorage.getItem('access_token')
}

const projectMindmapService = {
  // ============================================
  // PUBLIC ENDPOINTS
  // ============================================

  /**
   * Get all active projects (list view)
   * @returns {Promise<{status: string, data: Array}>}
   */
  async getProjects() {
    try {
      const endpoint = isAuthenticated()
        ? '/project-mindmaps'
        : '/project-mindmaps/public'
      const response = await api.get(endpoint)
      return response.data
    } catch {
      return { status: 'success', data: [] }
    }
  },

  /**
   * Get project detail by slug
   * @param {string} slug - Project slug
   * @returns {Promise<{status: string, data: Object}>}
   */
  async getProjectBySlug(slug) {
    try {
      const endpoint = isAuthenticated()
        ? `/project-mindmaps/${slug}`
        : `/project-mindmaps/public/${slug}`
      const response = await api.get(endpoint)
      return response.data
    } catch {
      return {
        status: 'error',
        data: null,
        message: 'Không thể tải thông tin dự án'
      }
    }
  },

  // ============================================
  // ADMIN ENDPOINTS
  // ============================================

  /**
   * Get all projects for admin (including hidden)
   * @param {Object} params - Filter params
   * @returns {Promise<{status: string, data: Array}>}
   */
  async getAdminProjects(params = {}) {
    try {
      const response = await api.get('/project-mindmaps/admin', { params })
      return response.data
    } catch {
      return { status: 'success', data: [] }
    }
  },

  /**
   * Get single project for admin editing
   * @param {number} projectId - Project ID
   * @returns {Promise<{status: string, data: Object}>}
   */
  async getAdminProject(projectId) {
    try {
      const response = await api.get(`/project-mindmaps/admin/${projectId}`)
      return response.data
    } catch {
      return {
        status: 'error',
        data: null,
        message: 'Không thể tải thông tin dự án'
      }
    }
  },

  /**
   * Create new project
   * @param {Object} data - Project data
   * @returns {Promise<{status: string, data: Object, message: string}>}
   */
  async createProject(data) {
    try {
      const response = await api.post('/project-mindmaps/admin', data)
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Tạo dự án thất bại'
      }
    }
  },

  /**
   * Update project
   * @param {number} projectId - Project ID
   * @param {Object} data - Updated data
   * @returns {Promise<{status: string, data: Object, message: string}>}
   */
  async updateProject(projectId, data) {
    try {
      const response = await api.put(`/project-mindmaps/admin/${projectId}`, data)
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Cập nhật dự án thất bại'
      }
    }
  },

  /**
   * Delete project
   * @param {number} projectId - Project ID
   * @returns {Promise<{status: string, message: string}>}
   */
  async deleteProject(projectId) {
    try {
      const response = await api.delete(`/project-mindmaps/admin/${projectId}`)
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Xóa dự án thất bại'
      }
    }
  },

  /**
   * Reorder projects (drag-drop)
   * @param {Array<{id: number, display_order: number}>} items
   * @returns {Promise<{status: string, message: string}>}
   */
  async reorderProjects(items) {
    try {
      const response = await api.put('/project-mindmaps/admin/reorder', { items })
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Sắp xếp thất bại'
      }
    }
  },

  /**
   * Sync projects from DXMT API
   * @returns {Promise<{status: string, data: Object, message: string}>}
   */
  async syncFromDXMT() {
    try {
      const response = await api.post('/project-mindmaps/admin/sync')
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Đồng bộ thất bại'
      }
    }
  },

  /**
   * Get sync status and history
   * @returns {Promise<{status: string, data: Object}>}
   */
  async getSyncStatus() {
    try {
      const response = await api.get('/project-mindmaps/admin/sync')
      return response.data
    } catch {
      return {
        status: 'success',
        data: { current: { status: 'unknown' }, history: [] }
      }
    }
  },

  /**
   * Toggle featured status
   * @param {number} projectId - Project ID
   * @returns {Promise<{status: string, data: Object}>}
   */
  async toggleFeatured(projectId) {
    try {
      const response = await api.post(`/project-mindmaps/admin/${projectId}/featured`)
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Thay đổi trạng thái thất bại'
      }
    }
  },

  /**
   * Toggle active/hidden status
   * @param {number} projectId - Project ID
   * @returns {Promise<{status: string, data: Object}>}
   */
  async toggleStatus(projectId) {
    try {
      const response = await api.post(`/project-mindmaps/admin/${projectId}/status`)
      return response.data
    } catch (error) {
      return {
        status: 'error',
        message: error.response?.data?.message || 'Thay đổi trạng thái thất bại'
      }
    }
  },

  // ============================================
  // HELPER FUNCTIONS
  // ============================================

  /**
   * Get branch icon
   * @param {string} branchKey - Branch key
   * @returns {string} Icon name for react-icons
   */
  getBranchIcon(branchKey) {
    const icons = {
      overview: 'HiOutlineInformationCircle',
      location: 'HiOutlineLocationMarker',
      design: 'HiOutlineTemplate',
      amenities: 'HiOutlineSparkles',
      pricing: 'HiOutlineCurrencyDollar',
      progress: 'HiOutlineChartBar'
    }
    return icons[branchKey] || 'HiOutlineFolder'
  },

  /**
   * Get branch color
   * @param {string} branchKey - Branch key
   * @returns {string} Tailwind color class
   */
  getBranchColor(branchKey) {
    const colors = {
      overview: 'bg-blue-500',
      location: 'bg-green-500',
      design: 'bg-purple-500',
      amenities: 'bg-amber-500',
      pricing: 'bg-red-500',
      progress: 'bg-cyan-500'
    }
    return colors[branchKey] || 'bg-gray-500'
  },

  /**
   * Get branch light color (for backgrounds)
   * @param {string} branchKey - Branch key
   * @returns {string} Tailwind color class
   */
  getBranchLightColor(branchKey) {
    const colors = {
      overview: 'bg-blue-50 border-blue-200',
      location: 'bg-green-50 border-green-200',
      design: 'bg-purple-50 border-purple-200',
      amenities: 'bg-amber-50 border-amber-200',
      pricing: 'bg-red-50 border-red-200',
      progress: 'bg-cyan-50 border-cyan-200'
    }
    return colors[branchKey] || 'bg-gray-50 border-gray-200'
  }
}

export default projectMindmapService
